import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
  Alert,
  Paper,
  Container,
  Link,
  useTheme,
  useMediaQuery
} from '@mui/material';
import LockResetIcon from '@mui/icons-material/LockReset';
import EmailIcon from '@mui/icons-material/Email';
import { doctorRequestPasswordReset } from '../../../redux/middleware/user/doctor/resetPassword';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email) {
      setError('Please enter your email address.');
      return;
    }

    setLoading(true);
    try {
      await doctorRequestPasswordReset(email);
      setSuccessMessage('Password reset instructions have been sent to your email address. Please check your inbox.');
      setError('');
      setEmail('');
    } catch (error) {
      setError('Failed to request password reset. Please verify your email address and try again.');
      setSuccessMessage('');
    } finally {
      setLoading(false);
    }
  };

  const textFieldStyles = {
    '& .MuiOutlinedInput-root': {
      '&:hover fieldset': {
        borderColor: '#5185ac'
      },
      '&.Mui-focused fieldset': {
        borderColor: '#13334c'
      }
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: '#13334c'
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        padding: 2
      }}
    >
      <Container maxWidth='sm'>
        <Paper
          elevation={6}
          sx={{
            p: { xs: 3, sm: 4 },
            borderRadius: 2,
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              mb: 3
            }}
          >
            <Box
              sx={{
                backgroundColor: 'rgba(19, 51, 76, 0.1)',
                borderRadius: '50%',
                p: 2,
                mb: 2
              }}
            >
              <LockResetIcon
                sx={{
                  fontSize: 40,
                  color: '#13334c'
                }}
              />
            </Box>
            <Typography
              variant={isMobile ? 'h4' : 'h3'}
              textAlign='center'
              color='#13334c'
              fontWeight='600'
              gutterBottom
            >
              Forgot Password
            </Typography>
            <Typography
              variant='body1'
              textAlign='center'
              color='text.secondary'
              sx={{ maxWidth: '90%', mb: 2 }}
            >
              Enter your email address below and we'll send you instructions to reset your password.
            </Typography>
          </Box>

          {error && (
            <Alert
              severity='error'
              sx={{ mb: 2, borderRadius: 1 }}
            >
              {error}
            </Alert>
          )}

          {successMessage && (
            <Alert
              severity='success'
              sx={{ mb: 2, borderRadius: 1 }}
            >
              {successMessage}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              label='Email Address'
              type='email'
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              fullWidth
              required
              margin='normal'
              sx={textFieldStyles}
              InputProps={{
                startAdornment: (
                  <EmailIcon sx={{ mr: 1, color: '#5185ac' }} />
                )
              }}
              placeholder='doctor@example.com'
            />

            <Button
              type='submit'
              variant='contained'
              fullWidth
              disabled={loading}
              sx={{
                mt: 3,
                mb: 2,
                py: 1.5,
                backgroundColor: '#13334c',
                '&:hover': {
                  backgroundColor: '#0d2638'
                },
                fontSize: '1rem',
                textTransform: 'none'
              }}
            >
              {loading ? <CircularProgress size={24} color='inherit' /> : 'Send Reset Instructions'}
            </Button>

            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Link
                href='#'
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/app/doctor/login');
                }}
                sx={{
                  color: '#5185ac',
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                }}
              >
                Return to Login
              </Link>
            </Box>
          </form>
        </Paper>

        <Typography
          variant='body2'
          color='text.secondary'
          align='center'
          sx={{ mt: 4 }}
        >
          Need help? Contact support at support@yourmedicalapp.com
        </Typography>
      </Container>
    </Box>
  );
};

export default ForgotPassword;
